import { DataPlaneResourcePathPrefix } from '../constants/data-plane';
import { StringifiedPattern } from '../constants/patterns';
import { isValidDataPlaneUri, parseDataPlanePath } from '../ids/data-plane';
import { ClientError } from '../models/common';
import { DataPlaneIdTokenSet } from '../models/data-plane';
import { isUndefinedOrWhiteSpace } from '../utilities/string';
import { tryOrDefault } from '../utilities/try-or-default';

/* eslint-disable security/detect-non-literal-regexp */
// Justification: regex is non-literal, but is not constructed from user input.
const environmentPathPattern = new RegExp(
    `^/${DataPlaneResourcePathPrefix.Project}/${StringifiedPattern.ResourceName}/${DataPlaneResourcePathPrefix.User}/${StringifiedPattern.Guid}/${DataPlaneResourcePathPrefix.Environment}/${StringifiedPattern.ResourceName}$`,
    'i'
);
/* eslint-enable security/detect-non-literal-regexp */

export const createEnvironmentDataPlaneUri = (
    tokens: DataPlaneIdTokenSet<'devCenter' | 'environmentName' | 'projectName' | 'user'>
): string => {
    const { devCenter, environmentName, projectName, user } = tokens;

    if (isUndefinedOrWhiteSpace(devCenter)) {
        throw new ClientError('Dev center token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(environmentName)) {
        throw new ClientError('Environment name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(projectName)) {
        throw new ClientError('Project name token cannot be undefined or white space');
    }

    if (isUndefinedOrWhiteSpace(user)) {
        throw new ClientError('User token cannot be undefined or white space');
    }

    const path = `/${[
        DataPlaneResourcePathPrefix.Project,
        projectName.toLowerCase(),
        DataPlaneResourcePathPrefix.User,
        user.toLowerCase(),
        DataPlaneResourcePathPrefix.Environment,
        environmentName.toLowerCase(),
    ].join('/')}`;

    const uri = new URL(path, devCenter);

    return `${uri}`;
};

export const getTokensFromEnvironmentDataPlaneUri = (
    uri: string
): DataPlaneIdTokenSet<'devCenter' | 'environmentName' | 'projectName' | 'user'> => {
    if (!isValidEnvironmentDataPlaneUri(uri)) {
        throw new ClientError('Given URI was not a valid Data Plane URI for an environment');
    }

    const { origin, pathname } = new URL(uri);
    const [, projectName, , user, , environmentName] = parseDataPlanePath(pathname);

    return {
        devCenter: origin,
        environmentName,
        projectName,
        user,
    };
};

export const isValidEnvironmentDataPlaneUri = (uri: string): boolean =>
    isValidDataPlaneUri(uri, environmentPathPattern);

export const tryCreateEnvironmentDataPlaneUri = tryOrDefault(createEnvironmentDataPlaneUri);
export const tryGetTokensFromEnvironmentDataPlaneUri = tryOrDefault(getTokensFromEnvironmentDataPlaneUri);
